/**
 * GroupObservationsCard — group proxy reads on the programme outcome report (VER-115).
 *
 * Shaped by Sean's PR #5 review. A group proxy read is one facilitator's reading of a whole group
 * in one session; it carries no participant, so it can neither be paired into the baseline/endpoint
 * statistics nor be split into individual rows. Before this the programme report simply did not
 * show it, which meant a Model B group's only measurements were invisible on its own report.
 *
 * It is rendered as a SEPARATE, labelled section for that reason — never beside the paired figures
 * as if it were the same kind of number.
 *
 * SUPPRESSION: the server withholds the figures for any session whose group was below the floor
 * (a mean over two people is one person's score plus arithmetic). The count and the session still
 * show, because the fact a reading was taken is not itself disclosive and its absence would
 * misreport the programme as unassessed. This component renders that decision; it never makes it.
 */
import React from "react";
import { Alert, Box, Chip, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { VisibilityOffOutlined } from "@mui/icons-material";
import { VCard } from "../../../core/components/VCard";
import { GroupObservationScale } from "../../../client/api/survey";

const formatScore = (value: number | null): string => (value === null ? "—" : value.toFixed(1));

export type GroupObservationsCardProps = {
    scale: GroupObservationScale;
};

export const GroupObservationsCard = ({ scale }: GroupObservationsCardProps): JSX.Element => {
    const suppressedSessions = scale.sessions.filter((session) => session.suppressed).length;

    return (
        <VCard sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                <Typography variant="h5" component="h3" sx={{ mr: "auto" }}>
                    {scale.scaleName}
                </Typography>
                <Chip size="small" label="Facilitator-recorded" color="warning" variant="outlined" />
            </Box>

            <Typography variant="body2" color="textSecondary" mb={2}>
                {scale.observations} group {scale.observations === 1 ? "observation" : "observations"} recorded by a
                facilitator on the group&apos;s behalf. These are not participants&apos; own answers and are not
                included in the paired baseline/endpoint results above.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Session</TableCell>
                        <TableCell align="right">People in session</TableCell>
                        <TableCell align="right">Observations</TableCell>
                        <TableCell align="right">Mean</TableCell>
                        <TableCell align="right">Range</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {scale.sessions.map((session) => (
                        <TableRow key={session.sessionId ?? "unassigned"}>
                            <TableCell>
                                {session.sessionNumber === null ? "Not recorded" : `Session ${session.sessionNumber}`}
                            </TableCell>
                            <TableCell align="right">{session.groupSize}</TableCell>
                            <TableCell align="right">{session.observations}</TableCell>
                            <TableCell align="right">
                                {session.suppressed ? (
                                    <VisibilityOffOutlined fontSize="small" color="disabled" titleAccess="Withheld" />
                                ) : (
                                    formatScore(session.mean)
                                )}
                            </TableCell>
                            <TableCell align="right">
                                {session.suppressed ? "—" : `${formatScore(session.min)}–${formatScore(session.max)}`}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {suppressedSessions > 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    Figures are hidden for {suppressedSessions}{" "}
                    {suppressedSessions === 1 ? "session with fewer" : "sessions with fewer"} than{" "}
                    {scale.minimumReportableGroup} people. At that size a group figure can reveal an individual&apos;s
                    own score. The observations themselves are still listed.
                </Alert>
            ) : null}
        </VCard>
    );
};

export default GroupObservationsCard;
